import { useState, useRef, useCallback } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { COLUMN_ORDER_KEY } from "@/components/tableau/utils/constants";

export function useRowOperations({ orders, setOrders, markRowAsModified, columnOrder, setColumnOrder, columns }) {
  // Row drag & drop
  const [draggedRow, setDraggedRow] = useState(null);
  const [dragOverRow, setDragOverRow] = useState(null);
  // Column drag & drop
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  // Import
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  // ---- ROW ACTIONS ----
  const handleAddRow = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/orders/create-empty`, { method: "POST" });
      if (response.ok) {
        const newOrder = await response.json();
        setOrders(prev => [newOrder, ...prev]);
        markRowAsModified(newOrder.order_id);
        toast.success("Nouvelle ligne ajoutée");
      }
    } catch { toast.error("Erreur lors de l'ajout"); }
  }, [setOrders, markRowAsModified]);

  const handleDuplicateRow = useCallback(async (orderId) => {
    try {
      const response = await authFetch(`${API}/orders/${orderId}/duplicate`, { method: "POST" });
      if (response.ok) {
        const newOrder = await response.json();
        const idx = orders.findIndex(o => o.order_id === orderId);
        setOrders(prev => { const updated = [...prev]; updated.splice(idx + 1, 0, newOrder); return updated; });
        markRowAsModified(newOrder.order_id);
        toast.success("Ligne dupliquée");
      }
    } catch { toast.error("Erreur lors de la duplication"); }
  }, [orders, setOrders, markRowAsModified]);

  const handleDeleteRow = useCallback(async (orderId) => {
    try {
      const response = await authFetch(`${API}/orders/${orderId}`, { method: "DELETE" });
      if (response.ok) { setOrders(prev => prev.filter(o => o.order_id !== orderId)); toast.success("Ligne supprimée"); }
      else { const err = await response.json().catch(() => ({})); toast.error(err.detail || `Erreur ${response.status}`); }
    } catch { toast.error("Erreur lors de la suppression"); }
  }, [setOrders]);

  const handleMoveRow = useCallback(async (orderId, direction) => {
    const currentIdx = orders.findIndex(o => o.order_id === orderId);
    const newIdx = direction === 'up' ? currentIdx - 1 : currentIdx + 1;
    if (newIdx < 0 || newIdx >= orders.length) return;
    const newOrders = [...orders];
    [newOrders[currentIdx], newOrders[newIdx]] = [newOrders[newIdx], newOrders[currentIdx]];
    setOrders(newOrders);
    try { await authFetch(`${API}/orders/reorder`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ order_ids: newOrders.map(o => o.order_id) }) }); }
    catch (e) { console.error("Failed to save order:", e); }
  }, [orders, setOrders]);

  // ---- ROW DRAG & DROP ----
  const handleDragStart = useCallback((e, orderId) => { setDraggedRow(orderId); e.dataTransfer.effectAllowed = 'move'; }, []);
  const handleDragOver = useCallback((e, orderId) => { e.preventDefault(); if (draggedRow !== orderId) setDragOverRow(orderId); }, [draggedRow]);
  const handleDragLeave = useCallback(() => setDragOverRow(null), []);
  const handleDrop = useCallback(async (e, targetOrderId) => {
    e.preventDefault();
    setDragOverRow(null);
    if (!draggedRow || draggedRow === targetOrderId) { setDraggedRow(null); return; }
    const draggedIdx = orders.findIndex(o => o.order_id === draggedRow);
    const targetIdx = orders.findIndex(o => o.order_id === targetOrderId);
    const newOrders = [...orders];
    const [removed] = newOrders.splice(draggedIdx, 1);
    newOrders.splice(targetIdx, 0, removed);
    setOrders(newOrders);
    setDraggedRow(null);
    try { await authFetch(`${API}/orders/reorder`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ order_ids: newOrders.map(o => o.order_id) }) }); toast.success("Ordre mis à jour"); }
    catch (e) { console.error("Failed to save order:", e); }
  }, [draggedRow, orders, setOrders]);
  const handleDragEnd = useCallback(() => { setDraggedRow(null); setDragOverRow(null); }, []);

  // ---- COLUMN DRAG & DROP ----
  const handleColumnDragStart = useCallback((e, columnName) => { setDraggedColumn(columnName); e.dataTransfer.effectAllowed = 'move'; }, []);
  const handleColumnDragOver = useCallback((e, columnName) => { e.preventDefault(); if (draggedColumn && draggedColumn !== columnName) setDragOverColumn(columnName); }, [draggedColumn]);
  const handleColumnDragLeave = useCallback(() => setDragOverColumn(null), []);
  const handleColumnDrop = useCallback((e, targetColumnName) => {
    e.preventDefault();
    setDragOverColumn(null);
    if (!draggedColumn || draggedColumn === targetColumnName) { setDraggedColumn(null); return; }
    const draggedIdx = columnOrder.indexOf(draggedColumn);
    const targetIdx = columnOrder.indexOf(targetColumnName);
    const newOrder = [...columnOrder];
    newOrder.splice(draggedIdx, 1);
    newOrder.splice(targetIdx, 0, draggedColumn);
    setColumnOrder(newOrder);
    setDraggedColumn(null);
    toast.success("Colonnes réorganisées");
  }, [draggedColumn, columnOrder, setColumnOrder]);
  const handleColumnDragEnd = useCallback(() => { setDraggedColumn(null); setDragOverColumn(null); }, []);

  const resetColumnOrder = useCallback(() => {
    const originalOrder = columns.map(c => c.name);
    setColumnOrder(originalOrder);
    localStorage.removeItem(COLUMN_ORDER_KEY);
    toast.success("Ordre des colonnes réinitialisé");
  }, [columns, setColumnOrder]);

  // ---- IMPORT ----
  const handleImportClick = useCallback((withPreview) => {
    if (fileInputRef.current) { fileInputRef.current.dataset.preview = withPreview ? "true" : "false"; fileInputRef.current.click(); }
  }, []);

  const executeImport = useCallback(async (file) => {
    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await authFetch(`${API}/orders/import`, { method: "POST", body: formData });
      if (response.ok) {
        const data = await response.json();
        data.orders.forEach(order => markRowAsModified(order.order_id));
        setOrders(prev => [...data.orders, ...prev]);
        toast.success(`${data.imported_count} ligne(s) importée(s)`);
        setShowImportModal(false);
        setImportPreview(null);
      } else { const err = await response.json(); toast.error(err.detail || "Erreur lors de l'import"); }
    } catch { toast.error("Erreur lors de l'import"); }
    finally { setIsImporting(false); }
  }, [setOrders, markRowAsModified]);

  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const withPreview = e.target.dataset.preview === "true";
    if (!file.name.endsWith('.xlsx')) { toast.error("Seuls les fichiers Excel (.xlsx) sont acceptés"); return; }
    if (withPreview) {
      setIsImporting(true);
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await authFetch(`${API}/orders/import/preview`, { method: "POST", body: formData });
        if (response.ok) { const data = await response.json(); setImportPreview({ ...data, file }); setShowImportModal(true); }
        else { const err = await response.json(); toast.error(err.detail || "Erreur"); }
      } catch { toast.error("Erreur lors de l'aperçu"); }
      finally { setIsImporting(false); }
    } else { await executeImport(file); }
    e.target.value = '';
  }, [executeImport]);

  const handleConfirmImport = useCallback(() => {
    if (importPreview?.file) executeImport(importPreview.file);
  }, [importPreview, executeImport]);

  return {
    draggedRow, dragOverRow, draggedColumn, dragOverColumn,
    showImportModal, setShowImportModal, importPreview, setImportPreview, isImporting,
    fileInputRef,
    handleAddRow, handleDuplicateRow, handleDeleteRow, handleMoveRow,
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
    handleColumnDragStart, handleColumnDragOver, handleColumnDragLeave, handleColumnDrop, handleColumnDragEnd,
    resetColumnOrder, handleImportClick, handleFileSelect, handleConfirmImport,
  };
}
